/**
 * PromoBanner — polls /promotions and surfaces active unread/undismissed
 * promotions using the 5-template visual system.
 *
 * • BannerStrip: slide-down top strip (first promo)
 * • PopupCard: bottom-right card stack (additional promos)
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import api from '../services/api'
import { PromoPopupCard, PromoBannerStrip } from './PromoTemplates'

const POLL_INTERVAL = 5 * 60 * 1000

async function fetchPromos() {
  const res = await api.get('/promotions')
  return Array.isArray(res.data) ? res.data : []
}
async function markRead(id) { await api.post(`/promotions/${id}/read`).catch(() => {}) }
async function dismissPromo(id) { await api.post(`/promotions/${id}/dismiss`).catch(() => {}) }

// ─── Top banner strip ─────────────────────────────────────────────────────────

function BannerStrip({ promo, onDismiss, onCTA }) {
  const [visible, setVisible] = useState(false)
  useEffect(() => { const t = setTimeout(() => setVisible(true), 80); return () => clearTimeout(t) }, [promo.id])

  const handleDismiss = () => { setVisible(false); setTimeout(() => onDismiss(promo.id), 300) }
  const handleCTA     = () => { setVisible(false); setTimeout(() => onCTA(promo), 200) }

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[60] transition-transform duration-300 ease-out"
      style={{ transform: visible ? 'translateY(0)' : 'translateY(-100%)' }}
    >
      <PromoBannerStrip
        promo={promo}
        onDismiss={handleDismiss}
        onCTA={handleCTA}
      />
    </div>
  )
}

// ─── Bottom-right popup stack ─────────────────────────────────────────────────

function PopupStack({ promos, onDismiss, onCTA }) {
  const [idx, setIdx] = useState(0)
  const [visible, setVisible] = useState(false)

  useEffect(() => { const t = setTimeout(() => setVisible(true), 500); return () => clearTimeout(t) }, [])

  const promo = promos[idx]
  if (!promo) return null

  const handleDismiss = (id) => {
    onDismiss(id)
    if (idx >= promos.length - 1) setVisible(false)
  }
  const handleHide = () => { setVisible(false); setTimeout(() => onDismiss(promo.id), 300) }

  return (
    <div
      className="fixed bottom-8 right-4 z-[59] w-72 transition-all duration-300 ease-out"
      style={{
        transform: visible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
      }}
    >
      <PromoPopupCard
        promo={promo}
        onDismiss={handleHide}
        onCTA={onCTA}
      />

      {/* Multi-promo pagination */}
      {promos.length > 1 && (
        <div className="flex items-center justify-center gap-2 mt-2">
          <button onClick={() => setIdx(i => Math.max(0, i - 1))} disabled={idx === 0}
            className="text-gray-600 hover:text-gray-400 disabled:opacity-30 transition-colors">
            <ChevronLeft size={14}/>
          </button>
          <div className="flex gap-1">
            {promos.map((_, i) => (
              <div key={i} onClick={() => setIdx(i)}
                className={`w-1.5 h-1.5 rounded-full cursor-pointer transition-all ${i === idx ? 'bg-white scale-125' : 'bg-white/40'}`}/>
            ))}
          </div>
          <button onClick={() => setIdx(i => Math.min(promos.length - 1, i + 1))} disabled={idx === promos.length - 1}
            className="text-gray-600 hover:text-gray-400 disabled:opacity-30 transition-colors">
            <ChevronRight size={14}/>
          </button>
        </div>
      )}
    </div>
  )
}

// ─── Main export ─────────────────────────────────────────────────────────────

export default function PromoBanner() {
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [promos,    setPromos]    = useState([])
  const [dismissed, setDismissed] = useState(new Set())

  const load = useCallback(async () => {
    if (!isAuthenticated) return
    try {
      const all = await fetchPromos()
      const active = all.filter(p => !p.dismissed)
      setPromos(active)
      active.filter(p => !p.read).forEach(p => markRead(p.id))
    } catch { /* non-critical */ }
  }, [isAuthenticated])

  useEffect(() => {
    load()
    const t = setInterval(load, POLL_INTERVAL)
    return () => clearInterval(t)
  }, [load])

  const handleDismiss = (id) => {
    dismissPromo(id)
    setPromos(prev => prev.filter(p => p.id !== id))
    setDismissed(prev => new Set([...prev, id]))
  }

  const handleCTA = (promo) => {
    markRead(promo.id)
    handleDismiss(promo.id)
    if (promo.cta_link) navigate(promo.cta_link)
  }

  const visible = promos.filter(p => !dismissed.has(p.id))
  if (!isAuthenticated || visible.length === 0) return null

  const [banner, ...rest] = visible

  return (
    <>
      <BannerStrip key={banner.id} promo={banner} onDismiss={handleDismiss} onCTA={handleCTA}/>
      {rest.length > 0 && (
        <PopupStack promos={rest} onDismiss={handleDismiss} onCTA={handleCTA}/>
      )}
    </>
  )
}
